import { faInfoCircle, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IBankAccount } from "interfaces/IBankAccount"
import { useMemo, useState } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { BankAccount } from "./BankAccount";
import { AxiosInstance } from "axios";
import { ModalHandler } from "components/general/ModalHandler";
import { changeBankAccountStatus, setDefaultBankAccount } from "api/auth";
import { LoadingScreenHandler } from "components/general/LoadingScreenHandler";
import { RequestStatesTypes } from "types/RequestsStatesTypes";
import { useModalHandler } from "hooks/useModalHandler";
import { primaryColor, primaryColorOpacity } from "utils/colors";

/**
 * Custom props of the bank accounts component
 */
interface Props {
    /**
     * The bank accounts state
     */
    bankAccounts: IBankAccount[];
    /**
     * Axios instance needed for some api calls on children components
     */
    axiosInstance: AxiosInstance;
    /**
     * update accounts callback function to pass it to parent components
     */
    updateAccounts: () => void;
}

/**
 * Component that displays the many bank accounts on a list
 */
export const BankAccounts = ({ bankAccounts, axiosInstance, updateAccounts }: Props) => {
    const [isFavoriteBankAccount, setIsFavoriteBankAccount] = useState<RequestStatesTypes>("none");
    const [isRemovedCollapsed, setIsRemovedCollapsed] = useState<boolean>(true);
    const { modal, initialModal, errorModal, setModal, closeModal } = useModalHandler({ initialMessage: "Estás apunto de eliminar una cuenta o tarjeta para pagos.\n¿Deseas continuar?" });

    /**
     * sets the chosen bank account to the default one
     */
    const onSetDefaultAccount = async (accountId: number) => {
        setIsFavoriteBankAccount("loading");
        const { data, result } = await setDefaultBankAccount(axiosInstance, accountId);
        if (data) {
            updateAccounts();
            return;
        }
        setModal(errorModal);
        setIsFavoriteBankAccount("failure");
    }

    /**
     * shows the modal to prompt a confirmation and then attempt to remove a bank account
     */
    const onRemoveAccount = async (accountId: number) => {
        setModal({
            ...initialModal,
            show: true,
            onConfirm() { removeAccount(accountId) },
            onHide() { closeModal() },
        });
    }

    /**
     * makes the endpoint call to remove the bank account from the active list
     */
    const recoverAccount = async (accountId: number) => {
        setIsFavoriteBankAccount("loading");
        const { data, result } = await changeBankAccountStatus(axiosInstance, accountId);
        if (data) {
            updateAccounts();
            return;
        }
        setModal(errorModal);
        setIsFavoriteBankAccount("failure");
    }

    /**
     * makes the endpoint call to remove the bank account from the active list
     */
    const removeAccount = async (accountId: number) => {
        const { data, result } = await changeBankAccountStatus(axiosInstance, accountId);
        if (data) {
            updateAccounts();
            return;
        }
        setModal(errorModal);
    }

    /**
     * Returns an empty form in case the information is not provided or the favorite bank account
     */
    const favoriteBankAccount = useMemo(() => {
        const defaultPayment = bankAccounts.find(acc => acc.is_default) || undefined;
        defaultPayment && setIsFavoriteBankAccount("successful");
        return defaultPayment;
    }, [bankAccounts]);

    /**
     * Returns the rest unfavorite bank accounts array
     */
    const restBankAccounts = useMemo(() => {
        const rest = bankAccounts.filter(bankAccount => (
            (!bankAccount.is_default && bankAccount.status)
        ));
        return rest;
    }, [bankAccounts]);

    /**
     * Returns the removed bank accounts array
     */
    const removedAccounts = useMemo(() => {
        const removed = bankAccounts.filter(bankAccount => (
            !bankAccount.status
        ));
        return removed;
    }, [bankAccounts]);

    return (
        <Col xs={12}>
            {bankAccounts.length > 0 &&
                <Col xs={12}>
                    <LoadingScreenHandler
                        loadingText="Estableciendo los cambios"
                        requestState={isFavoriteBankAccount}
                        successComponent={
                            <div className="mb-3">
                                {favoriteBankAccount &&
                                    <>
                                        <Form.Label>
                                            <strong>Cuenta o Tarjeta preferida</strong>
                                            <OverlayTrigger
                                                trigger={["hover", "focus"]}
                                                placement={"top"}
                                                overlay={
                                                    <Tooltip className="custom-tooltip text-unwrap">
                                                        Esta cuenta es la predeterminada donde se recibirán todos los ingresos.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <BankAccount
                                            isDefault={favoriteBankAccount.is_default}
                                            bankAccount={favoriteBankAccount}
                                        />
                                    </>
                                }
                            </div>
                        }
                    />
                    {restBankAccounts.length > 0 &&
                        <Form.Label>
                            <strong>Cuentas o Tarjetas secundarias</strong>
                            <OverlayTrigger
                                trigger={["hover", "focus"]}
                                placement={"top"}
                                overlay={
                                    <Tooltip className="custom-tooltip text-unwrap">
                                        Las cuentas secundarias funcionan cuando por alguna razón externa la cuenta predefinida no puede recibir ingresos.
                                    </Tooltip>
                                }
                            >
                                <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                            </OverlayTrigger>
                        </Form.Label>
                    }
                    {restBankAccounts?.map(bankAccount => (
                        <div className="mb-3" >
                            <BankAccount
                                bankAccount={bankAccount}
                                setDefaultAccount={onSetDefaultAccount}
                                changeStatus={onRemoveAccount}
                            />
                        </div>
                    ))}
                    {(removedAccounts && removedAccounts.length > 0) &&
                        <Row
                            className="m-1 p-2"
                            style={{
                                borderRadius: "5px",
                                border: `2px solid ${primaryColor}`,
                                boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.4)",
                                backgroundColor: primaryColorOpacity,
                            }}
                        >
                            <Row style={{ margin: "3px 0px -8px -22px" }} onClick={() => setIsRemovedCollapsed(!isRemovedCollapsed)}>
                                <div style={{ fontSize: 30 }}>
                                    <strong>Cuentas desactivadas</strong>
                                    {
                                        <FontAwesomeIcon
                                            icon={isRemovedCollapsed ? faSortDown : faSortUp}
                                            className="ms-2 align-self-center"
                                            style={{
                                                marginBottom: `${isRemovedCollapsed ? "3px" : "-9px"}`,
                                            }}
                                        />
                                    }
                                </div>
                            </Row>
                            <div className="mt-3" >
                                {!isRemovedCollapsed &&
                                    removedAccounts.map(account => (
                                        <BankAccount changeStatus={recoverAccount} containerClass="mb-2" bankAccount={account} isRecover={true} />
                                    ))
                                }
                            </div>
                        </Row>
                    }
                </Col>
            }
            <ModalHandler modal={modal} />
        </Col>
    )
}